// World scratch map ("World Map" tab): tap countries you've visited to
// scratch them off, like a physical scratch map. Self-contained like
// BucketView — owns its profile load/save. Persistence: optimistic local
// state + localStorage cache, debounced PUT /api/tp/profile (staging fetch
// rewrite applies). Map geometry is self-hosted TopoJSON in
// public/world-50m.json; countries resolve through data/countries.js.
import React from 'react';
import { ComposableMap, Geographies, Geography, ZoomableGroup } from 'react-simple-maps';
import { PALETTE } from '../theme';
import { Icon } from '../components/UI';
import { useAuth } from '../contexts/AuthContext';
import { apiFetchProfile, apiUpdateProfile } from '../utils/api';
import { COUNTRIES, countryForGeo, flagEmoji } from '../data/countries';
import { toggleCountry, computeStats, suggestedCountries } from '../utils/visited';

const GEO_URL = `${process.env.PUBLIC_URL || ''}/world-50m.json`;
const DISMISSED_KEY = 'tp_scratchDismissed_v1';
const SAVE_DEBOUNCE_MS = 1000;

const cacheKey = (uid) => `tp_visited_v1_${uid || 'anon'}`;
const readCache = (uid) => {
  try { return JSON.parse(localStorage.getItem(cacheKey(uid)) || '[]'); } catch { return []; }
};
const writeCache = (uid, list) => {
  try { localStorage.setItem(cacheKey(uid), JSON.stringify(list)); } catch {}
};
const readDismissed = () => {
  try { return JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]'); } catch { return []; }
};
const writeDismissed = (list) => {
  try { localStorage.setItem(DISMISSED_KEY, JSON.stringify(list)); } catch {}
};

// Foil-flake burst shown at the tap point when a country is scratched off
const BURST_KEYFRAMES = `
@keyframes tpScratchFly {
  0%   { transform: translate(0, 0) scale(1); opacity: 0.95; }
  100% { transform: translate(var(--dx), var(--dy)) scale(0.2); opacity: 0; }
}`;
const BURST_COLORS = ['#D9A441', PALETTE.accent, '#E8C9A0', PALETTE.accentDk, '#D9A441', '#C9932B'];

function ScratchBurst({ burst }) {
  if (!burst) return null;
  return (
    <div key={burst.key} aria-hidden="true" style={{ position: 'absolute', left: burst.x, top: burst.y, pointerEvents: 'none', zIndex: 5 }}>
      {BURST_COLORS.map((color, i) => {
        const angle = (i / BURST_COLORS.length) * Math.PI * 2 + 0.4;
        const dist = 22 + (i % 3) * 9;
        return (
          <span key={i} style={{
            position: 'absolute', width: 7, height: 7, borderRadius: i % 2 ? 1 : 999,
            background: color,
            '--dx': `${Math.cos(angle) * dist}px`,
            '--dy': `${Math.sin(angle) * dist}px`,
            animation: 'tpScratchFly 0.55s ease-out forwards',
          }} />
        );
      })}
    </div>
  );
}

export function WorldMapView({ isMobile, trips = [] }) {
  const { user } = useAuth();
  const px = isMobile ? 20 : 28;

  const [visited, setVisited] = React.useState(() => readCache(user?.uid));
  const [syncState, setSyncState] = React.useState('loading'); // loading | synced | saving | offline
  const [dismissed, setDismissed] = React.useState(() => readDismissed());
  const [query, setQuery] = React.useState('');
  const [tooltip, setTooltip] = React.useState(null); // { name, flag, visited, x, y }
  const [burst, setBurst] = React.useState(null);
  const [position, setPosition] = React.useState({ coordinates: [0, 0], zoom: 1 });

  const zoomBy = (factor) => setPosition(p => ({
    ...p,
    zoom: Math.min(10, Math.max(1, p.zoom * factor)),
  }));
  const resetZoom = () => setPosition({ coordinates: [0, 0], zoom: 1 });

  const visitedRef = React.useRef(visited);
  visitedRef.current = visited;
  const saveTimer = React.useRef(null);
  const containerRef = React.useRef(null);

  // Load profile from server; local cache already painted the map.
  React.useEffect(() => {
    let alive = true;
    if (!user) { setSyncState('offline'); return; }
    apiFetchProfile(user)
      .then(p => {
        if (!alive) return;
        setVisited(p.visitedCountries || []);
        writeCache(user.uid, p.visitedCountries || []);
        setSyncState('synced');
      })
      .catch(() => { if (alive) setSyncState('offline'); });
    return () => { alive = false; };
  }, [user]);

  const doSave = React.useCallback((attempt = 0) => {
    if (!user) return;
    setSyncState('saving');
    apiUpdateProfile(user, { visitedCountries: visitedRef.current })
      .then(() => setSyncState('synced'))
      .catch(() => {
        if (attempt === 0) setTimeout(() => doSave(1), 1500);
        else setSyncState('offline');
      });
  }, [user]);

  const scheduleSave = React.useCallback(() => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { saveTimer.current = null; doSave(); }, SAVE_DEBOUNCE_MS);
  }, [doSave]);

  // Flush a pending save when leaving the tab
  React.useEffect(() => () => {
    if (saveTimer.current) { clearTimeout(saveTimer.current); doSave(); }
  }, [doSave]);

  const applyVisited = (next) => {
    setVisited(next);
    writeCache(user?.uid, next);
    scheduleSave();
  };

  const toggle = (a2, evt) => {
    const next = toggleCountry(visited, a2);
    const scratchedOn = next.length > visited.length;
    applyVisited(next);
    if (scratchedOn && evt && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setBurst({ x: evt.clientX - rect.left, y: evt.clientY - rect.top, key: Date.now() });
    }
  };

  const stats = computeStats(visited);
  const visitedSet = React.useMemo(() => new Set(visited), [visited]);
  const suggestions = React.useMemo(
    () => suggestedCountries(trips, visited, dismissed),
    [trips, visited, dismissed]
  );

  const dismissSuggestions = () => {
    const next = [...new Set([...dismissed, ...suggestions.map(c => c.a2)])];
    setDismissed(next);
    writeDismissed(next);
  };
  const addAllSuggestions = () => {
    applyVisited([...new Set([...visited, ...suggestions.map(c => c.a2)])]);
  };

  const results = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return COUNTRIES
      .filter(c => c.name.toLowerCase().includes(q) || c.aliases.some(a => a.includes(q)))
      .slice(0, 8);
  }, [query]);

  return (
    <div style={{ padding: `20px ${px}px` }}>
      <style>{BURST_KEYFRAMES}</style>

      {/* Stats header */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontFamily: 'Fraunces', fontSize: 30, color: PALETTE.ink, letterSpacing: -0.5 }}>
          {stats.count}<span style={{ fontSize: 17, color: PALETTE.inkMute }}> / {stats.total}</span>
        </div>
        <div style={{ fontFamily: 'JetBrains Mono', fontSize: 11, color: PALETTE.inkSoft, letterSpacing: 0.5, textTransform: 'uppercase' }}>
          countries scratched · {stats.pct}% of the world
        </div>
        <div style={{ fontFamily: 'Inter', fontSize: 11, color: PALETTE.inkMute, marginLeft: 'auto' }}>
          {syncState === 'saving' ? 'Saving…' : syncState === 'offline' ? 'Offline — changes saved on this device' : ''}
        </div>
      </div>
      <div style={{ height: 4, borderRadius: 999, background: PALETTE.ruleSoft, margin: '10px 0 16px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${Math.max(stats.pct, stats.count > 0 ? 2 : 0)}%`, background: `linear-gradient(90deg, ${PALETTE.accent}, #D9A441)`, borderRadius: 999, transition: 'width 0.4s' }} />
      </div>

      {/* Suggestions from past trips */}
      {suggestions.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          background: PALETTE.card, border: `0.5px solid ${PALETTE.ruleSoft}`,
          borderRadius: 12, padding: '10px 14px', marginBottom: 14,
        }}>
          <span style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkSoft }}>From your trips:</span>
          {suggestions.map(c => (
            <button key={c.a2} onClick={() => toggle(c.a2)} style={{
              appearance: 'none', cursor: 'pointer', border: `1px solid ${PALETTE.rule}`,
              background: PALETTE.paper, borderRadius: 999, padding: '4px 12px',
              fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <span aria-hidden="true">{flagEmoji(c.a2)}</span>{c.name}
              <Icon name="plus" size={12} stroke={2} />
            </button>
          ))}
          {suggestions.length > 1 && (
            <button onClick={addAllSuggestions} style={{
              appearance: 'none', cursor: 'pointer', border: 0, background: PALETTE.ink,
              color: PALETTE.paper, borderRadius: 999, padding: '5px 13px',
              fontFamily: 'Inter', fontSize: 12, fontWeight: 600,
            }}>Add all</button>
          )}
          <button onClick={dismissSuggestions} aria-label="Dismiss suggestions" style={{
            appearance: 'none', cursor: 'pointer', border: 0, background: 'transparent',
            color: PALETTE.inkMute, padding: 4, marginLeft: 'auto', display: 'flex',
          }}>
            <Icon name="x" size={14} />
          </button>
        </div>
      )}

      {/* Search — the escape hatch for countries too small to tap */}
      <div style={{ position: 'relative', marginBottom: 14, maxWidth: 420 }}>
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search a country to scratch it off…"
          aria-label="Search countries"
          style={{
            appearance: 'none', border: `1px solid ${PALETTE.rule}`, borderRadius: 10,
            padding: '10px 14px', width: '100%', boxSizing: 'border-box', outline: 'none',
            fontFamily: 'Inter', fontSize: 13, color: PALETTE.ink, background: PALETTE.card,
          }}
        />
        {results.length > 0 && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 8, marginTop: 4,
            background: PALETTE.card, border: `0.5px solid ${PALETTE.rule}`, borderRadius: 10,
            boxShadow: '0 6px 20px rgba(34,31,26,0.12)', overflow: 'hidden',
          }}>
            {results.map(c => {
              const isVisited = visitedSet.has(c.a2);
              return (
                <button key={c.a2} onClick={() => toggle(c.a2)} style={{
                  appearance: 'none', border: 0, cursor: 'pointer', width: '100%',
                  display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px',
                  background: 'transparent', textAlign: 'left',
                  fontFamily: 'Inter', fontSize: 13, color: PALETTE.ink,
                }}
                onMouseEnter={e => e.currentTarget.style.background = PALETTE.ruleSoft}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <span aria-hidden="true">{flagEmoji(c.a2)}</span>
                  <span style={{ flex: 1 }}>{c.name}</span>
                  {isVisited && <Icon name="check" size={14} stroke={2} style={{ color: PALETTE.accent }} />}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* The map */}
      <div ref={containerRef} style={{
        position: 'relative', background: PALETTE.card, borderRadius: 16,
        border: `0.5px solid ${PALETTE.ruleSoft}`, overflow: 'hidden',
      }}>
        <ComposableMap
          projection="geoNaturalEarth1"
          width={980}
          height={500}
          style={{ width: '100%', height: 'auto', display: 'block' }}
        >
          <ZoomableGroup
            maxZoom={10}
            zoom={position.zoom}
            center={position.coordinates}
            onMoveEnd={setPosition}
          >
            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies.map(geo => {
                  const country = countryForGeo(geo);
                  const isVisited = country && visitedSet.has(country.a2);
                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      tabIndex={-1}
                      aria-label={country ? country.name : geo.properties?.name}
                      onMouseEnter={(evt) => country && setTooltip({
                        name: country.name, flag: flagEmoji(country.a2),
                        visited: visitedSet.has(country.a2), x: evt.clientX, y: evt.clientY,
                      })}
                      onMouseMove={(evt) => setTooltip(t => t && { ...t, x: evt.clientX, y: evt.clientY })}
                      onMouseLeave={() => setTooltip(null)}
                      onClick={(evt) => country && toggle(country.a2, evt)}
                      style={{
                        default: {
                          fill: isVisited ? PALETTE.accent : PALETTE.paperDim,
                          stroke: isVisited ? PALETTE.accentDk : PALETTE.rule,
                          strokeWidth: 0.4, outline: 'none', transition: 'fill 0.35s',
                        },
                        hover: {
                          fill: isVisited ? PALETTE.accentDk : '#DCD3C0',
                          stroke: isVisited ? PALETTE.accentDk : PALETTE.rule,
                          strokeWidth: 0.5, outline: 'none',
                          cursor: country ? 'pointer' : 'default',
                        },
                        pressed: {
                          fill: isVisited ? PALETTE.accentDk : '#D9A441',
                          outline: 'none',
                        },
                      }}
                    />
                  );
                })
              }
            </Geographies>
          </ZoomableGroup>
        </ComposableMap>

        <ScratchBurst burst={burst} />

        {/* Zoom controls */}
        <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {[
            { label: 'Zoom in', icon: 'plus', onClick: () => zoomBy(1.5), disabled: position.zoom >= 10 },
            { label: 'Zoom out', icon: 'minus', onClick: () => zoomBy(1 / 1.5), disabled: position.zoom <= 1 },
            { label: 'Reset zoom', icon: 'globe', onClick: resetZoom, disabled: position.zoom <= 1 },
          ].map(btn => (
            <button key={btn.label} onClick={btn.onClick} disabled={btn.disabled} aria-label={btn.label} title={btn.label} style={{
              appearance: 'none', cursor: btn.disabled ? 'default' : 'pointer',
              width: 32, height: 32, borderRadius: 999,
              border: `1px solid ${PALETTE.rule}`, background: 'rgba(251,247,240,0.95)',
              color: btn.disabled ? PALETTE.inkMute : PALETTE.ink,
              opacity: btn.disabled ? 0.5 : 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 1px 4px rgba(34,31,26,0.12)',
            }}>
              <Icon name={btn.icon} size={15} stroke={2} />
            </button>
          ))}
        </div>

        <div style={{
          position: 'absolute', bottom: 10, right: 14, pointerEvents: 'none',
          fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, letterSpacing: 0.5,
        }}>
          {isMobile ? 'pinch to zoom · tap to scratch' : 'scroll to zoom · click to scratch'}
        </div>
      </div>

      {/* Hover tooltip (desktop) */}
      {tooltip && !isMobile && (
        <div style={{
          position: 'fixed', left: tooltip.x + 14, top: tooltip.y - 10, zIndex: 20,
          background: PALETTE.ink, color: PALETTE.paper, borderRadius: 8,
          padding: '6px 10px', pointerEvents: 'none',
          fontFamily: 'Inter', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6,
          boxShadow: '0 4px 14px rgba(34,31,26,0.25)',
        }}>
          <span aria-hidden="true">{tooltip.flag}</span>
          <span>{tooltip.name}</span>
          <span style={{ opacity: 0.6, fontSize: 11 }}>{tooltip.visited ? '· visited' : '· tap to scratch'}</span>
        </div>
      )}
    </div>
  );
}
